import React, { memo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Theme, alpha } from '@mui/material/styles';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import Download from '@mui/icons-material/Download';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { SwaggerFile } from '../../types/swaggerFile';
import { monoSx } from '../dashboard/styles';
import FileGlyph from './FileGlyph';
import { FILE_GRID_SX } from './gridLayout';
import { formatFileSize, formatUploaded } from './swaggerFileModel';

export interface SwaggerFileRowHandlers {
  onEdit: (file: SwaggerFile) => void;
  onDownload: (file: SwaggerFile) => void;
  onDelete: (file: SwaggerFile) => void;
}

interface SwaggerFileRowProps extends SwaggerFileRowHandlers {
  file: SwaggerFile;
  isNew: boolean;
  downloading: boolean;
}

const tagSx = {
  height: 24,
  px: 1,
  borderRadius: 1.5,
  bgcolor: 'action.hover',
  color: 'text.secondary',
  fontSize: 12,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
} as const;

const addTagsSx = {
  height: 24,
  pl: 0.5,
  pr: 1,
  gap: 0.25,
  borderRadius: 1.5,
  border: '1px dashed',
  borderColor: 'divider',
  color: 'text.disabled',
  fontSize: 12,
  '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
} as const;

const actionSx = { color: 'text.secondary' } as const;
const deleteActionSx = {
  color: 'text.secondary',
  '&:hover': { color: 'error.main', bgcolor: (theme: Theme) => alpha(theme.palette.error.main, 0.1) },
} as const;

const SwaggerFileRow: React.FC<SwaggerFileRowProps> = ({ file, isNew, downloading, onEdit, onDownload, onDelete }) => {
  const { day, time } = formatUploaded(file.uploadDate);
  const tags = file.tags ?? [];
  return (
    <Box
      role="row"
      sx={{
        ...FILE_GRID_SX,
        minHeight: 68,
        py: 1,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: isNew ? (theme) => alpha(theme.palette.primary.main, 0.05) : 'background.paper',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box role="cell" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <FileGlyph />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
          <Typography noWrap title={file.originalName} sx={{ ...monoSx, fontSize: 13, fontWeight: 500, color: 'text.primary' }}>
            {file.originalName}
          </Typography>
          <Typography noWrap sx={{ fontSize: 12, color: file.description ? 'text.secondary' : 'text.disabled' }}>
            {file.description || 'No description'}
          </Typography>
        </Box>
        {isNew ? (
          <Box
            component="span"
            sx={{ flexShrink: 0, height: 20, px: 1, borderRadius: 2.5, bgcolor: 'brand.container', color: 'brand.onContainer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center' }}
          >
            NEW
          </Box>
        ) : null}
      </Box>
      <Box role="cell" sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
        {tags.length ? (
          tags.map((tag) => (
            <Box component="span" key={tag} sx={tagSx}>
              {tag}
            </Box>
          ))
        ) : (
          <ButtonBase onClick={() => onEdit(file)} sx={addTagsSx}>
            <Add sx={{ fontSize: 14 }} />
            Add tags
          </ButtonBase>
        )}
      </Box>
      <Typography role="cell" sx={{ fontSize: 13, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
        {formatFileSize(file.size)}
      </Typography>
      <Box role="cell" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography sx={{ fontSize: 13, color: 'text.primary' }}>{day}</Typography>
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{time}</Typography>
      </Box>
      <Box role="cell" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
        <Tooltip title="Generate mapping document">
          <IconButton component={RouterLink} to="/mapping-document" aria-label="Generate mapping document" sx={{ color: 'primary.main' }}>
            <DescriptionOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <span>
            <IconButton aria-label={`Download ${file.originalName}`} onClick={() => onDownload(file)} disabled={downloading} sx={actionSx}>
              {downloading ? <CircularProgress size={18} color="inherit" /> : <Download fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Edit details">
          <IconButton aria-label={`Edit ${file.originalName}`} onClick={() => onEdit(file)} sx={actionSx}>
            <EditOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton aria-label={`Delete ${file.originalName}`} onClick={() => onDelete(file)} sx={deleteActionSx}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default memo(SwaggerFileRow);
